import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { motion } from 'framer-motion';
import { MapPin, Bed, Bath, Users, ArrowRight, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';

export const SimilarProperties = ({ currentProperty, language = 'en', maxItems = 4 }) => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const texts = {
    pt: {
      title: 'Imóveis Similares',
      subtitle: 'Você também pode gostar',
      viewProperty: 'Ver Imóvel',
      perNight: '/noite',
      bedrooms: 'quartos',
      bathrooms: 'banheiros',
      guests: 'hóspedes',
      noSimilar: 'Nenhum imóvel similar encontrado',
      exploreAll: 'Explorar Todos os Imóveis'
    },
    en: {
      title: 'Similar Properties',
      subtitle: 'You might also like',
      viewProperty: 'View Property',
      perNight: '/night',
      bedrooms: 'bedrooms',
      bathrooms: 'bathrooms',
      guests: 'guests',
      noSimilar: 'No similar properties found',
      exploreAll: 'Explore All Properties'
    }
  };

  const t = texts[language] || texts.en;

  useEffect(() => {
    fetchSimilarProperties();
  }, [currentProperty]);

  const fetchSimilarProperties = async () => {
    try {
      setLoading(true);
      
      // Build search params based on current property
      const params = new URLSearchParams();
      if (currentProperty?.city) params.append('city', currentProperty.city);
      if (currentProperty?.bedrooms) {
        params.append('min_bedrooms', Math.max(1, currentProperty.bedrooms - 1));
        params.append('max_bedrooms', currentProperty.bedrooms + 1);
      }
      if (currentProperty?.nightly_price_suggestion) {
        const price = (currentProperty.price || currentProperty.nightly_price_suggestion);
        params.append('min_price', Math.max(0, price * 0.7));
        params.append('max_price', price * 1.3);
      }
      params.append('limit', maxItems + 1); // Get one extra to filter out current

      const response = await api.get(`/search/properties?${params.toString()}`);
      const filtered = (response.data || []).filter(
        p => p.id !== currentProperty?.id
      ).slice(0, maxItems);
      
      setProperties(filtered);
    } catch (error) {
      // Silently fail - similar properties is not critical
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#00C2D1]" />
          <h3 className="text-xl font-bold text-gray-900">{t.title}</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(maxItems)].map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="h-40 w-full" />
              <CardContent className="p-4 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-1/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="bg-gradient-to-r from-slate-50 to-cyan-50 rounded-2xl p-8 text-center">
        <Sparkles className="w-10 h-10 text-[#00C2D1] mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">{t.noSimilar}</h3>
        <Button
          onClick={() => navigate('/search')}
          className="mt-4 bg-[#00C2D1] hover:bg-[#00A8B5] text-white"
        >
          {t.exploreAll}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#00C2D1]" />
          <div>
            <h3 className="text-xl font-bold text-gray-900">{t.title}</h3>
            <p className="text-sm text-gray-500">{t.subtitle}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={() => navigate('/search')}
          className="text-[#00C2D1] hover:text-[#00A8B5]"
        >
          {t.exploreAll}
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {properties.map((property, index) => (
          <motion.div
            key={property.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <Card
              className="overflow-hidden cursor-pointer group hover:shadow-lg transition-all duration-300"
              onClick={() => navigate(`/property/${property.id}`)}
            >
              <div className="relative h-40 overflow-hidden">
                {property.images?.[0] ? (
                  <img
                    src={property.images[0].image_url || property.images[0].url || property.images[0]}
                    alt={property.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <MapPin className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <Badge className="bg-white/90 text-gray-900 font-bold">
                    ${(property.price || property.nightly_price_suggestion)}{t.perNight}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <h4 className="font-semibold text-gray-900 line-clamp-1 mb-2 group-hover:text-[#00C2D1] transition-colors">
                  {property.title}
                </h4>
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <MapPin className="w-3 h-3 mr-1" />
                  <span className="line-clamp-1">{property.city}, {property.state}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-600">
                  <span className="flex items-center gap-1">
                    <Bed className="w-3 h-3" />
                    {property.bedrooms}
                  </span>
                  <span className="flex items-center gap-1">
                    <Bath className="w-3 h-3" />
                    {property.bathrooms}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {property.max_guests}
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default SimilarProperties;
